import { useMemo, useState } from "react";
import { useTournamentContext } from "../../../../hook/context";
import {
  EditTournamentBaseWrapper,
  EditTournamentInputBox,
  EditTournamentInputWrapper,
  EditTournamentSettingBox,
  EditTournamentSettingInputBox,
  EditTournamentSettingPagination,
  EditTournamentSettingTitle,
  EditTournamentSettingWrapper,
} from "./style";
import {
  EarlyChipSettingType,
  TournamentType,
} from "../../../../hook/useEditTournament";
import TournamentDropBox from "../dropbox";

const earlyChipSettingTypes = [
  { name: "없음", value: EarlyChipSettingType.NONE },
  { name: "인원 제한", value: EarlyChipSettingType.USER_LIMIT },
  { name: "레벨 제한", value: EarlyChipSettingType.LEVEL_LIMIT },
];

const tournamentDiscountTypes = [
  { name: "없음", value: false },
  { name: "금액 할인", value: true },
];

const tournamentTypes = [
  { name: "GTD", value: TournamentType.Tournament },
  { name: "데일리", value: TournamentType.Daily },
  { name: "이벤트", value: TournamentType.Milestone },
];

const EditTournamentBase = () => {
  const {
    tournamentId,
    title,
    setTitle,
    subTitle,
    setSubTitle,
    registerDeadlineLevel,
    setRegisterDeadlineLevel,
    tournamentType,
    setTournamentType,
    startDate,
    setStartDate,
    startTime,
    setStartTime,
    entry,
    setEntry,
    earlyChipSetting,
    setEarlyChipSetting,
    earlyInChip,
    setEarlyInChip,
    userLimit,
    setUserLimit,
    levelLimitChip,
    setLevelLimitChip,
    isTournamentDiscount,
    setIsTournamentDiscount,
    tournamentDiscounts,
    setTournamentDiscounts,
    selectedPresetId,
    setPrizeType,
    setBuyInChip,
    setReBuyInChip,
    setAdOnChip,
  } = useTournamentContext();
  // 드롭박스 오픈
  const [isTypeOpen, setIsTypeOpen] = useState(false);
  const [isEarlyOpen, setIsEarlyOpen] = useState(false);
  const [isDiscountOpen, setIsDiscountOpen] = useState(false);

  // 레벨제한 얼리칩 조정
  const [levelCurrentPage, setLevelCurrentPage] = useState(1);
  const levelTotalPage = useMemo(
    () => Math.max(1, Math.ceil(levelLimitChip?.length / 3)),
    [levelLimitChip?.length],
  );
  const levelLimitChipFilter = useMemo(
    () =>
      levelLimitChip.slice((levelCurrentPage - 1) * 3, levelCurrentPage * 3),
    [levelLimitChip, levelCurrentPage],
  );

  // 할인 설정 조정
  const [discountCurrentPage, setDiscountCurrentPage] = useState(1);
  const discountTotalPage = useMemo(
    () => Math.max(1, Math.ceil(tournamentDiscounts?.length / 3)),
    [tournamentDiscounts?.length],
  );
  const tournamentDiscountFilter = useMemo(
    () =>
      tournamentDiscounts.slice(
        (discountCurrentPage - 1) * 3,
        discountCurrentPage * 3,
      ),
    [tournamentDiscounts, discountCurrentPage],
  );

  const handleOpenType = () => {
    if (tournamentId) {
      alert("토너먼트 게시 후 토너먼트 형태를 수정할 수 없습니다. ");
      return;
    }
    if (selectedPresetId) {
      alert("프리셋을 불러온 후 토너먼트 형태를 수정할 수 없습니다. ");
      return;
    }
    setIsTypeOpen(!isTypeOpen);
  };

  // 레벨 제한 조정 ==============================================================
  const handleLevelLimitChip = (value: number) => {
    const data = [...levelLimitChip];
    if (value < 0) {
      if (levelLimitChip.length === 1) return;
      data.pop();
      if (data.length % 3 === 0 && levelCurrentPage === levelTotalPage) {
        setLevelCurrentPage(levelCurrentPage - 1);
      }
    } else {
      data.push({
        level: levelLimitChip.length + 1,
        chip: 0,
      });
    }
    setLevelLimitChip(data);
  };

  const levelNextPage = (page: number) => {
    if (levelCurrentPage + page === 0) return;
    if (levelCurrentPage + page > levelTotalPage) return;
    setLevelCurrentPage(levelCurrentPage + page);
  };

  const handleLevelInput = (value: string, index: number) => {
    const rawValue = value.replace(/[^0-9]/g, "");
    const newChip = parseInt(rawValue, 10) || 0;
    const updatedLevelLimitChip = [...levelLimitChip];
    updatedLevelLimitChip[(levelCurrentPage - 1) * 3 + index] = {
      ...updatedLevelLimitChip[(levelCurrentPage - 1) * 3 + index],
      chip: newChip,
    };
    setLevelLimitChip(updatedLevelLimitChip);
  };

  // 할인 설정 조정 ==============================================================
  const discountNextPage = (page: number) => {
    if (discountCurrentPage + page === 0) return;
    if (discountCurrentPage + page > discountTotalPage) return;
    setDiscountCurrentPage(discountCurrentPage + page);
  };

  const handleDiscounts = (value: number) => {
    const data = [...tournamentDiscounts];
    if (value < 0) {
      if (tournamentDiscounts.length === 1) return;
      if (tournamentDiscounts?.length === 1) return;
      data.pop();
      if (data.length % 3 === 0 && discountCurrentPage === discountTotalPage) {
        setDiscountCurrentPage(discountCurrentPage - 1);
      }
    } else {
      data.push({
        level: tournamentDiscounts?.length + 1,
        amount: 0,
      });
    }
    setTournamentDiscounts(data);
  };

  const handleDiscountInput = (value: string, index: number) => {
    const rawValue = value.replace(/[^0-9]/g, "");
    const updatedDiscounts = [...tournamentDiscounts];
    updatedDiscounts[(discountCurrentPage - 1) * 3 + index] = {
      ...updatedDiscounts[(discountCurrentPage - 1) * 3 + index],
      amount: +rawValue,
    };
    setTournamentDiscounts(updatedDiscounts);
  };

  const handleTournamentType = (type: TournamentType) => {
    setTournamentType(type);
    setBuyInChip(50000);
    setReBuyInChip(50000);
    setAdOnChip(50000);

    switch (type) {
      case TournamentType.Tournament:
        setPrizeType("SEED");
        break;
      case TournamentType.Daily:
        setPrizeType("%");
        break;
      case TournamentType.Milestone:
        break;
    }
  };

  return (
    <EditTournamentBaseWrapper
      onClick={() => {
        setIsDiscountOpen(false);
        setIsEarlyOpen(false);
        setIsTypeOpen(false);
      }}
    >
      <EditTournamentInputWrapper>
        <EditTournamentInputBox>
          <div className="name">토너먼트 타이틀</div>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="토너먼트 타이틀을 입력해주세요."
          />
        </EditTournamentInputBox>
        <EditTournamentInputBox>
          <div className="name">토너먼트 서브 타이틀</div>
          <input
            value={subTitle}
            onChange={(e) => setSubTitle(e.target.value)}
            placeholder="서브 타이틀을 입력해주세요."
          />
        </EditTournamentInputBox>
        <EditTournamentInputBox>
          <div className="name">레지 마감(LV)</div>
          <input
            value={registerDeadlineLevel}
            onChange={(e) => setRegisterDeadlineLevel(+e.target.value)}
          />
          <span>LV</span>
        </EditTournamentInputBox>
        <EditTournamentInputBox>
          <div className="name">토너먼트 형태</div>
          <TournamentDropBox
            value={tournamentType}
            setValue={handleTournamentType}
            isOpen={isTypeOpen}
            setIsOpen={setIsTypeOpen}
            list={tournamentTypes}
            handleOpen={handleOpenType}
          />
        </EditTournamentInputBox>
        <EditTournamentInputBox>
          <div className="name">토너먼트 날짜</div>
          <input
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
            }}
            disabled={tournamentId ? true : false}
          />
        </EditTournamentInputBox>
        <EditTournamentInputBox>
          <div className="name">토너먼트 시간</div>
          <input
            type="time"
            value={startTime}
            onChange={(e) => {
              setStartTime(e.target.value);
            }}
          />
        </EditTournamentInputBox>
        <EditTournamentInputBox>
          <div className="name">기준 엔트리</div>
          <input value={entry} onChange={(e) => setEntry(+e.target.value)} />
          <span>엔트리</span>
        </EditTournamentInputBox>
        <EditTournamentInputBox>
          <div className="name">얼리버드 설정</div>
          <TournamentDropBox
            value={earlyChipSetting}
            setValue={setEarlyChipSetting}
            isOpen={isEarlyOpen}
            setIsOpen={setIsEarlyOpen}
            list={earlyChipSettingTypes}
          />
        </EditTournamentInputBox>
        <EditTournamentInputBox>
          <div className="name">얼리버드 할인 설정</div>
          <TournamentDropBox
            value={isTournamentDiscount}
            setValue={setIsTournamentDiscount}
            isOpen={isDiscountOpen}
            setIsOpen={setIsDiscountOpen}
            list={tournamentDiscountTypes}
          />
        </EditTournamentInputBox>
      </EditTournamentInputWrapper>
      <EditTournamentSettingWrapper>
        {earlyChipSetting === EarlyChipSettingType.USER_LIMIT && (
          <EditTournamentSettingBox height="96px">
            <EditTournamentSettingInputBox>
              <div className="name">인원제한</div>
              <input
                value={userLimit.toLocaleString()}
                onChange={(e) => {
                  const rawValue = e.target.value.replace(/[^0-9]/g, "");
                  setUserLimit(+rawValue);
                }}
              />
              <span>명까지 제공</span>
            </EditTournamentSettingInputBox>
            <EditTournamentSettingInputBox>
              <div className="name">얼리칩</div>
              <input
                value={earlyInChip.toLocaleString()}
                onChange={(e) => {
                  const rawValue = e.target.value.replace(/[^0-9]/g, "");
                  setEarlyInChip(+rawValue);
                }}
              />
              <span>칩</span>
            </EditTournamentSettingInputBox>
          </EditTournamentSettingBox>
        )}
        {earlyChipSetting === EarlyChipSettingType.LEVEL_LIMIT && (
          <EditTournamentSettingBox height="200px">
            <EditTournamentSettingTitle>
              얼리인 정보 설정
              <img
                src="/image-web/tournament/minus.svg"
                onClick={() => {
                  handleLevelLimitChip(-1);
                }}
                alt="minus"
              />
              {levelLimitChip.length}
              <img
                src="/image-web/tournament/plus.svg"
                onClick={() => {
                  handleLevelLimitChip(+1);
                }}
                alt="plus"
              />
              레벨 까지 제공
            </EditTournamentSettingTitle>
            {levelLimitChipFilter.map((l, i) => (
              <EditTournamentSettingInputBox key={i}>
                <div className="name">{l.level} LV</div>
                <input
                  value={l.chip === 0 ? "" : l.chip.toLocaleString()}
                  onChange={(e) => handleLevelInput(e.target.value, i)}
                />
                <span>칩</span>
              </EditTournamentSettingInputBox>
            ))}
            <EditTournamentSettingPagination>
              <img
                src="/image-web/tournament/early-page-left.svg"
                onClick={() => {
                  levelNextPage(-1);
                }}
                alt=""
              />
              <div className="page">
                {levelCurrentPage}/{levelTotalPage}
              </div>
              <img
                src="/image-web/tournament/early-page-right.svg"
                onClick={() => {
                  levelNextPage(1);
                }}
                alt=""
              />
            </EditTournamentSettingPagination>
          </EditTournamentSettingBox>
        )}
        {isTournamentDiscount && (
          <EditTournamentSettingBox height="200px">
            <EditTournamentSettingTitle>
              금액 할인 설정
              <img
                src="/image-web/tournament/minus.svg"
                onClick={() => {
                  handleDiscounts(-1);
                }}
                alt="minus"
              />
              {tournamentDiscounts.length}
              <img
                src="/image-web/tournament/plus.svg"
                onClick={() => {
                  handleDiscounts(+1);
                }}
                alt="plus"
              />
              레벨까지 제공
            </EditTournamentSettingTitle>
            {tournamentDiscountFilter.map((t, i) => (
              <EditTournamentSettingInputBox key={i}>
                <div className="name">{t.level} LV</div>
                <input
                  value={t.amount !== 0 ? t.amount.toLocaleString() : ""}
                  onChange={(e) => handleDiscountInput(e.target.value, i)}
                />
                <span>원</span>
              </EditTournamentSettingInputBox>
            ))}
            <EditTournamentSettingPagination>
              <img
                src="/image-web/tournament/early-page-left.svg"
                onClick={() => {
                  discountNextPage(-1);
                }}
                alt=""
              />
              <div className="page">
                {discountCurrentPage}/{discountTotalPage}
              </div>
              <img
                src="/image-web/tournament/early-page-right.svg"
                onClick={() => {
                  discountNextPage(1);
                }}
                alt=""
              />
            </EditTournamentSettingPagination>
          </EditTournamentSettingBox>
        )}
      </EditTournamentSettingWrapper>
    </EditTournamentBaseWrapper>
  );
};

export default EditTournamentBase;
